import React, { useState, useEffect, useMemo } from 'react';
import styles from './styles/StepCompanyInfo.module.css';
import inputStyles from './styles/OnboardingInput.module.css';
import sharedStyles from './styles/BrandSignup.module.css';
import { Luna } from './Luna';

const StepCompanyInfo = ({ brandSignup, onComplete, onSkipToDashboard, loading, error, onBack, canGoBack }) => {
  const [formData, setFormData] = useState({
    years_in_business: '',
    market_segment: '',
    similar_brands: '',
    business_size: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [enableSubmit, setEnableSubmit] = useState(false);

  // Market segment options with icons
  const marketSegments = [
    { value: 'Mass Market', label: 'Mass Market', icon: '🛒' },
    { value: 'Mid Range', label: 'Mid Range', icon: '👔' },
    { value: 'Luxury', label: 'Luxury', icon: '🍾' }
  ];

  // Business size options with icons
  const businessSizes = [
    { value: 'Startup', label: 'Startup', icon: '🚀' },
    { value: 'Mid-sized', label: 'Mid-sized', icon: '🏢' },
    { value: 'Large', label: 'Large', icon: '🌐' }
  ];

  useEffect(() => {
    // Pre-fill if we have step data
    const stepData = brandSignup.step_data?.['company_info'];
    if (stepData) {
      setFormData(prev => ({
        ...prev,
        years_in_business: stepData.years_in_business || '',
        market_segment: stepData.market_segment || '',
        similar_brands: stepData.similar_brands || '',
        business_size: stepData.business_size || ''
      }));
    }
  }, [brandSignup.step_data]);

  useEffect(() => {
    const isComplete =
      formData.years_in_business.trim().length > 0 &&
      !isNaN(formData.years_in_business) &&
      parseInt(formData.years_in_business) >= 0 &&
      formData.market_segment.length > 0 &&
      formData.business_size.length > 0;

    if (isComplete && Object.keys(formErrors).length === 0) {
      setEnableSubmit(true);
    } else {
      setEnableSubmit(false);
    }
  }, [formData, formErrors]);

  const validateForm = () => {
    const errors = {};

    if (!formData.years_in_business.trim()) {
      errors.years_in_business = 'Years in business is required';
    } else if (isNaN(formData.years_in_business) || parseInt(formData.years_in_business) < 0) {
      errors.years_in_business = 'Please enter a valid number';
    }

    if (!formData.market_segment) {
      errors.market_segment = 'Market segment is required';
    }

    if (!formData.business_size) {
      errors.business_size = 'Business size is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onComplete(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Memoize Luna message to prevent re-typing on form changes
  const lunaMessage = useMemo(() => [
    "Hi, I'm Luna, your Nearshore sourcing expert.",
    "Tell me a little about your company so I can pair you with the right nearshoring partners."
  ], []);

  // Memoize Luna component to prevent re-renders on form state changes
  const memoizedLuna = useMemo(() =>
    <Luna lunaDialogs={lunaMessage} />,
    [lunaMessage]
  );

  return (
    <div className={styles.stepContainer}>
      {memoizedLuna}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.twoColumnSection}>
          <div className={styles.formField}>
            <label className={styles.label}>How many years in business?</label>
            <input
              className={`${inputStyles.input} ${formErrors.years_in_business ? inputStyles.error : ''}`}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="3"
              value={formData.years_in_business}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                handleInputChange('years_in_business', value);
              }}
              disabled={loading}
              min="0"
            />
            {formErrors.years_in_business && (
              <p className={styles.errorMessage}>{formErrors.years_in_business}</p>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.label}>Do you have any inspiration brands?</label>
            <input
              className={`${inputStyles.input} ${formErrors.similar_brands ? inputStyles.error : ''}`}
              type="text"
              placeholder="Everlane, Warby Parker, Bonobos, Skims"
              value={formData.similar_brands}
              onChange={(e) => handleInputChange('similar_brands', e.target.value)}
              disabled={loading}
            />
            {formErrors.similar_brands && (
              <p className={styles.errorMessage}>{formErrors.similar_brands}</p>
            )}
          </div>
        </div>

        <div className={styles.twoColumnSection}>
          <div className={styles.formField}>
            <label className={styles.label}>What market segment do you serve?</label>
            <div className={styles.radioCardGroup}>
              {marketSegments.map((segment) => (
                <label
                  key={segment.value}
                  className={`${styles.radioCard} ${formData.market_segment === segment.value ? styles.radioCardSelected : ''}`}
                >
                  <input
                    type="radio"
                    name="market_segment"
                    value={segment.value}
                    checked={formData.market_segment === segment.value}
                    onChange={(e) => handleInputChange('market_segment', e.target.value)}
                    disabled={loading}
                    className={styles.hiddenRadio}
                  />
                  <span className={styles.radioIcon}>{segment.icon}</span>
                  <span className={styles.radioText}>{segment.label}</span>
                  {formData.market_segment === segment.value && (
                    <span className={styles.checkmark}>✓</span>
                  )}
                </label>
              ))}
            </div>
            {formErrors.market_segment && (
              <p className={styles.errorMessage}>{formErrors.market_segment}</p>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.label}>What is the size of your business?</label>
            <div className={styles.radioCardGroup}>
              {businessSizes.map((size) => (
                <label
                  key={size.value}
                  className={`${styles.radioCard} ${formData.business_size === size.value ? styles.radioCardSelected : ''}`}
                >
                  <input
                    type="radio"
                    name="business_size"
                    value={size.value}
                    checked={formData.business_size === size.value}
                    onChange={(e) => handleInputChange('business_size', e.target.value)}
                    disabled={loading}
                    className={styles.hiddenRadio}
                  />
                  <span className={styles.radioIcon}>{size.icon}</span>
                  <span className={styles.radioText}>{size.label}</span>
                  {formData.business_size === size.value && (
                    <span className={styles.checkmark}>✓</span>
                  )}
                </label>
              ))}
            </div>
            {formErrors.business_size && (
              <p className={styles.errorMessage}>{formErrors.business_size}</p>
            )}
          </div>
        </div>

        <button
          className={styles.button}
          type="submit"
          disabled={loading || !enableSubmit}
        >
          {loading ? 'Processing...' : 'Continue'}
        </button>

        {onSkipToDashboard && (
          <button
            type="button"
            className={sharedStyles.skipLink}
            onClick={onSkipToDashboard}
            disabled={loading}
          >
            I'll set up my project later. Take me to the Dashboard
          </button>
        )}
      </form>
    </div>
  );
};

export default StepCompanyInfo;
